import type { ItemDetailResponse } from "@kaidoki/proto";
import { Edit3, Trash2, User, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteItem,
  getNotificationSettings,
  NetworkError,
  purchaseItem,
  removeItemImage,
  snoozeItem,
  updateItem,
  uploadItemImage,
} from "../api/client";
import { useGroup } from "../contexts/GroupContext";
import { useItemsCache } from "../contexts/ItemsCacheContext";
import { useSubscription } from "../contexts/SubscriptionContext";
import {
  trackItemDelete,
  trackItemEdit,
  trackItemPurchase,
} from "../lib/analytics";
import { formatDate, formatDateTime } from "../lib/format";
import {
  getDaysUntilDue,
  getItemStatus,
  getProgressPercentage,
  statusConfig,
} from "../lib/status";
import { BottomSheet } from "./BottomSheet";
import { ItemForm } from "./ItemForm";

type Props = {
  itemId: string | null;
  initialAction?: string | null;
  onClose: () => void;
  onSuccess?: (message: string) => void;
};

export function ItemDetailSheet({
  itemId,
  initialAction,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation(["items", "common"]);
  const { activeGroupId } = useGroup();
  const { isPro } = useSubscription();
  const { removeItem, getItemDetail, replaceItem, updateDueDate } =
    useItemsCache();
  const data = itemId ? getItemDetail(itemId, activeGroupId) : null;
  const [purchasing, setPurchasing] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [preferredHours, setPreferredHours] = useState<number[]>();
  const [hoursLoading, setHoursLoading] = useState(true);

  useEffect(() => {
    if (!itemId) return;
    getNotificationSettings()
      .then((s) => setPreferredHours(s.preferredHours))
      .catch(() => {})
      .finally(() => setHoursLoading(false));
  }, [itemId]);

  useEffect(() => {
    if (itemId) {
      setShowEdit(false);
      setShowDelete(false);
      setShowAllHistory(false);
      autoPurchaseTriggered.current = false;
    }
  }, [itemId]);

  const autoPurchaseTriggered = useRef(false);
  useEffect(() => {
    if (
      data &&
      initialAction === "purchase" &&
      !autoPurchaseTriggered.current
    ) {
      autoPurchaseTriggered.current = true;
      handlePurchase();
    }
  });

  useEffect(() => {
    if (itemId) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [itemId]);

  function alertForError(err: unknown, fallbackKey: string) {
    if (err instanceof NetworkError) {
      alert(t("common:error.network"));
    } else {
      alert(t(fallbackKey));
    }
  }

  async function handlePurchase() {
    if (!itemId || !activeGroupId) return;
    setPurchasing(true);
    try {
      const result = await purchaseItem(activeGroupId, itemId);
      updateDueDate(
        itemId,
        result.nextDueAt,
        result.purchaseEvent,
        activeGroupId,
      );
      trackItemPurchase(itemId);
      onSuccess?.(
        t("items:purchase.success", {
          name: data?.item?.name,
          defaultValue: `「${data?.item?.name ?? ""}」の購入を記録しました`,
        }),
      );
    } catch (err) {
      alertForError(err, "items:errors.purchase");
    } finally {
      setPurchasing(false);
    }
  }

  async function handleSnooze(days: number) {
    if (!itemId || !activeGroupId) return;
    try {
      const result = await snoozeItem(activeGroupId, itemId, days);
      updateDueDate(itemId, result.nextDueAt, undefined, activeGroupId);
    } catch (err) {
      alertForError(err, "items:errors.snooze");
    }
  }

  async function handleUpdate(formData: {
    name: string;
    intervalDays: number;
    nextDueAt: string;
    notificationOffsets: string;
    memo: string;
    imageFile?: File;
    removeImage: boolean;
  }) {
    if (!itemId || !activeGroupId) return;
    setEditLoading(true);
    try {
      let updated = await updateItem(activeGroupId, itemId, {
        name: formData.name,
        intervalDays: formData.intervalDays,
        nextDueAt: formData.nextDueAt,
        notificationOffsets: formData.notificationOffsets
          .split(",")
          .map(Number)
          .filter((n) => !Number.isNaN(n)),
        memo: formData.memo || "",
      });
      if (formData.imageFile) {
        updated = await uploadItemImage(
          activeGroupId,
          itemId,
          formData.imageFile,
        );
      } else if (formData.removeImage) {
        updated = await removeItemImage(activeGroupId, itemId);
      }
      replaceItem(updated, activeGroupId);
      trackItemEdit(itemId);
      setShowEdit(false);
    } catch (err) {
      alertForError(err, "items:errors.update");
    } finally {
      setEditLoading(false);
    }
  }

  async function handleDelete() {
    if (!itemId || !activeGroupId) return;
    try {
      await deleteItem(activeGroupId, itemId);
      trackItemDelete(itemId);
      removeItem(itemId, activeGroupId);
      onClose();
    } catch (err) {
      alertForError(err, "items:errors.delete");
    }
  }

  if (!itemId) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-end justify-center">
        <button
          type="button"
          className="absolute inset-0 bg-black/40"
          onClick={onClose}
          aria-label={t("common:buttons.close")}
        />
        <div
          className="relative flex w-full max-w-lg flex-col rounded-t-3xl bg-white animate-slide-up"
          style={{ maxHeight: "90dvh" }}
        >
          {/* Drag Handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="h-1 w-10 rounded-full bg-gray-300/40" />
          </div>

          {/* Header */}
          <div className="border-b border-gray-200 px-4 pb-3">
            <div className="flex items-center justify-between">
              <h2 className="truncate text-lg font-semibold text-gray-900">
                {data?.item?.name ?? t("items:detail.title")}
              </h2>
              <div className="flex items-center gap-0.5">
                {data?.item && (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowEdit(true)}
                      className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
                    >
                      <Edit3 size={20} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDelete(true)}
                      className="rounded-full p-2 text-red-400 hover:bg-red-50"
                    >
                      <Trash2 size={20} />
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full p-2 text-gray-400 hover:bg-gray-100"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-4">
            {data?.item ? (
              <ItemDetailContent
                data={data}
                onSnooze={handleSnooze}
                onShowAllHistory={() => setShowAllHistory(true)}
                snoozingOrPurchasing={purchasing}
                t={t}
              />
            ) : null}
          </div>

          {/* Fixed bottom purchase button */}
          {data?.item && (
            <div
              className="border-t border-gray-200 px-4 pt-3"
              style={{
                paddingBottom:
                  "max(0.75rem, env(safe-area-inset-bottom, 0.75rem))",
              }}
            >
              <button
                type="button"
                onClick={handlePurchase}
                disabled={purchasing}
                className="flex h-11 w-full items-center justify-center rounded-lg bg-blue-500 text-sm font-semibold text-white transition-colors hover:bg-blue-600 disabled:opacity-50"
              >
                {purchasing
                  ? t("items:purchase.purchasing")
                  : t("items:purchase.button")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sub-sheets for actions */}
      {data?.item && (
        <>
          <BottomSheet
            open={showEdit}
            onClose={() => setShowEdit(false)}
            title={t("items:edit.title")}
          >
            <ItemForm
              initialData={{
                name: data.item.name,
                intervalDays: data.item.intervalDays,
                nextDueAt: data.item.nextDueAt,
                notificationOffsets: data.item.notificationOffsets.join(","),
                memo: data.item.memo ?? "",
              }}
              imageEditing={{ iconUrl: data.item.iconUrl, isPro }}
              onSubmit={handleUpdate}
              submitLabel={t("common:buttons.update")}
              loading={editLoading}
              preferredHours={preferredHours}
              preferredHoursLoading={hoursLoading}
            />
          </BottomSheet>

          <BottomSheet
            open={showDelete}
            onClose={() => setShowDelete(false)}
            title={t("items:delete.title")}
          >
            <p className="mb-4 text-sm text-gray-600">
              {t("items:delete.description", { name: data.item.name })}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowDelete(false)}
                className="flex-1 rounded-lg border border-gray-300 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                {t("common:buttons.cancel")}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="flex-1 rounded-lg border border-red-500 py-3 text-sm font-semibold text-red-500 transition-colors hover:bg-red-50"
              >
                {t("common:buttons.delete")}
              </button>
            </div>
          </BottomSheet>

          <BottomSheet
            open={showAllHistory}
            onClose={() => setShowAllHistory(false)}
            title={t("items:detail.purchaseHistory")}
          >
            <HistoryList data={data} showAll />
          </BottomSheet>
        </>
      )}
    </>
  );
}

function ItemDetailContent({
  data,
  onSnooze,
  onShowAllHistory,
  snoozingOrPurchasing,
  t,
}: {
  data: ItemDetailResponse;
  onSnooze: (days: number) => void;
  onShowAllHistory: () => void;
  snoozingOrPurchasing: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const item = data.item as NonNullable<ItemDetailResponse["item"]>;
  const status = getItemStatus(item.nextDueAt);
  const config = statusConfig[status];
  const daysUntil = getDaysUntilDue(item.nextDueAt);
  const progress = getProgressPercentage(item.nextDueAt, item.intervalDays);
  const isOverdue = status === "overdue";

  return (
    <div className="py-3">
      {/* Due info row */}
      <div className="flex items-center">
        <span className="text-[13px] text-gray-500">
          {formatDate(item.nextDueAt)}
        </span>
        <span className="mx-2 text-[13px] text-gray-300">·</span>
        <span
          className={`text-[13px] font-semibold ${isOverdue ? "text-red-600" : "text-gray-700"}`}
        >
          {daysUntil < 0
            ? t("items:card.daysOverdue", { count: Math.abs(daysUntil) })
            : daysUntil === 0
              ? t("items:card.dueToday")
              : t("items:card.daysLeft", { count: daysUntil })}
        </span>
        <span className="ml-auto text-xs text-gray-400">
          {t("common:units.days", { count: item.intervalDays })}
        </span>
      </div>

      {/* Progress bar */}
      <div className="mt-2 h-[3px] w-full overflow-hidden rounded-sm bg-gray-100">
        <div
          className={`h-full rounded-sm transition-all ${config.barColor}`}
          style={{
            width: `${progress}%`,
            opacity: Math.max(0.15, progress / 100),
          }}
        />
      </div>

      {/* Snooze */}
      <div className="mt-4">
        <p className="mb-1.5 text-[13px] font-semibold text-gray-500">
          {t("items:snooze.title")}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onSnooze(3)}
            disabled={snoozingOrPurchasing}
            className="rounded-lg border border-gray-300 px-4 py-2 text-[13px] text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            {t("items:snooze.plus3")}
          </button>
          <button
            type="button"
            onClick={() => onSnooze(7)}
            disabled={snoozingOrPurchasing}
            className="rounded-lg border border-gray-300 px-4 py-2 text-[13px] text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            {t("items:snooze.plus7")}
          </button>
        </div>
      </div>

      {/* Notification timing */}
      <div className="mt-5">
        <p className="mb-1 text-[13px] font-semibold text-gray-500">
          {t("items:detail.notificationTiming")}
        </p>
        <p className="text-[13px] text-gray-700">
          {item.notificationOffsets
            .map((d) =>
              d === 0
                ? t("items:detail.sameDay")
                : t("items:detail.daysBefore", { count: d }),
            )
            .join(", ")}
        </p>
      </div>

      {/* Memo */}
      {item.memo && (
        <div className="mt-3">
          <p className="mb-1 text-[13px] font-semibold text-gray-500">
            {t("items:detail.memo")}
          </p>
          <p className="text-[13px] text-gray-700">{item.memo}</p>
        </div>
      )}

      <div className="mt-5 rounded-[14px] border border-[#e3e8f0] p-3">
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className="text-[13px] font-semibold text-gray-600">
            {t("items:detail.purchaseHistory")}
          </p>
          <span className="rounded-full bg-[#edf3ff] px-2.5 py-1 text-[11px] font-bold text-[#285bbf]">
            {t("items:detail.historyCount", {
              count: data.purchaseHistory.length,
              defaultValue: `${data.purchaseHistory.length}件`,
            })}
          </span>
        </div>
        <HistoryList data={data} />
        {data.purchaseHistory.length > 3 && (
          <button
            type="button"
            onClick={onShowAllHistory}
            className="mt-2 h-10 w-full rounded-[10px] text-xs font-bold text-[#285bbf] hover:bg-[#edf3ff]"
          >
            {t("items:detail.viewAllHistory", {
              defaultValue: "履歴をすべて見る",
            })}
          </button>
        )}
      </div>
    </div>
  );
}

function HistoryList({
  data,
  showAll = false,
}: {
  data: ItemDetailResponse;
  showAll?: boolean;
}) {
  const { t } = useTranslation("items");
  const events = showAll
    ? data.purchaseHistory
    : data.purchaseHistory.slice(0, 3);

  if (events.length === 0) {
    return (
      <p className="py-3 text-xs text-gray-400">
        {t("detail.noHistory", { defaultValue: "まだ購入履歴がありません" })}
      </p>
    );
  }

  return (
    <ul className="divide-y divide-[#e3e8f0]">
      {events.map((event) => (
        <li key={event.id} className="flex min-h-12 items-center gap-2 py-2">
          {event.pictureUrl ? (
            <img
              src={event.pictureUrl}
              alt={event.displayName}
              className="h-8 w-8 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#edf3ff]">
              <User size={15} className="text-[#285bbf]" />
            </div>
          )}
          <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-700">
            {event.displayName}
          </span>
          <span className="shrink-0 text-[11px] tabular-nums text-gray-400">
            {formatDateTime(event.purchasedAt)}
          </span>
        </li>
      ))}
    </ul>
  );
}
